import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { FiArrowLeft, FiBook, FiEdit3 } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import documentacoesService, {
  Documentacao,
} from "../../services/documentacoes";
import Skeleton from "../../components/ui/Skeleton";
import AuthenticatedImage from "../../components/ui/AuthenticatedImage";
import DocumentacaoEditModal from "../../components/documentacoes/DocumentacaoEditModal";
import { ImageModal } from "../../components/conversations/ImageModal";

function DocumentacaoDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getUser } = useAuth();
  const user = getUser();
  const isAdmin = !!user?.is_admin;
  const [loading, setLoading] = useState(false);
  const [documentacao, setDocumentacao] = useState<Documentacao | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const loadDocumentacao = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await documentacoesService.getById(id);
      setDocumentacao(data);
    } catch (error) {
      console.error("Erro ao carregar documentação:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadDocumentacao();
    }
  }, [id, loadDocumentacao]);

  // Componente personalizado para renderizar imagens no markdown
  const MarkdownImage = ({ src, alt }: { src?: string; alt?: string }) => {
    if (!src) return null;

    const handleImageClick = (imageUrl: string) => {
      setSelectedImage(imageUrl);
    };

    // Se for uma URL completa, usa diretamente
    if (src.startsWith("http://") || src.startsWith("https://")) {
      return (
        <img
          src={src}
          alt={alt || ""}
          onClick={() => handleImageClick(src)}
          className="w-full rounded-xl my-3 shadow-md max-h-[220px] object-contain cursor-pointer hover:opacity-90 transition-opacity sm:my-4 sm:max-h-[320px] md:max-h-[400px]"
        />
      );
    }

    // Se for um caminho relativo, usa AuthenticatedImage com wrapper clicável
    return (
      <ClickableAuthenticatedImage
        fileName={src}
        alt={alt || ""}
        onImageClick={handleImageClick}
        className="w-full rounded-xl my-3 shadow-md max-h-[220px] object-contain sm:my-4 sm:max-h-[320px] md:max-h-[400px]"
      />
    );
  };

  // Componente wrapper para AuthenticatedImage clicável
  const ClickableAuthenticatedImage = ({
    fileName,
    alt,
    onImageClick,
    className,
  }: {
    fileName: string;
    alt: string;
    onImageClick: (url: string) => void;
    className?: string;
  }) => {
    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      // Tenta encontrar a imagem dentro do container clicado
      const img = e.currentTarget.querySelector("img");
      if (img?.src) {
        onImageClick(img.src);
      } else if (
        fileName.startsWith("http://") ||
        fileName.startsWith("https://")
      ) {
        onImageClick(fileName);
      } else {
        // Para caminhos relativos, constrói a URL da API
        const baseUrl = "https://api.appeasyfit.com.br";
        onImageClick(`${baseUrl}/images/download-public/${fileName}`);
      }
    };

    return (
      <div
        onClick={handleClick}
        className="cursor-pointer"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick(e as unknown as React.MouseEvent<HTMLDivElement>);
          }
        }}
      >
        <AuthenticatedImage
          fileName={fileName}
          alt={alt}
          className={`${className} hover:opacity-90 transition-opacity`}
        />
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-7xl px-3 py-4 sm:px-4 sm:py-6 md:px-6 2xl:px-10 pb-20 md:pb-6">
      {/* Header - Mobile First */}
      <div className="mb-3 sm:mb-6">
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-purple-600 via-purple-600 to-purple-700 p-3 shadow-lg sm:rounded-2xl sm:p-6">
          <div className="absolute inset-0 bg-grid-pattern opacity-10" aria-hidden />
          <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-white/10 blur-3xl sm:h-40 sm:w-40" aria-hidden />
          <div className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-white/10 blur-3xl sm:h-40 sm:w-40" aria-hidden />

          <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2.5 sm:gap-4 min-w-0 flex-1">
              <button
                type="button"
                onClick={() => navigate("/documentacoes")}
                className="flex-shrink-0 flex items-center justify-center rounded-xl bg-white/20 p-2.5 backdrop-blur-sm transition-colors min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 sm:p-3 hover:bg-white/30 active:scale-95 touch-manipulation"
                aria-label="Voltar para documentações"
              >
                <FiArrowLeft className="text-white" size={22} />
              </button>
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-lg font-bold text-white sm:text-2xl md:text-3xl">
                  {loading
                    ? "Carregando..."
                    : documentacao?.title || "Documentação"}
                </h2>
                <p className="mt-0.5 text-xs text-purple-100 sm:text-sm">
                  Manual do Sistema
                </p>
              </div>
            </div>
            {isAdmin && documentacao && (
              <button
                type="button"
                onClick={() => setIsEditModalOpen(true)}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/20 px-4 py-2.5 text-sm font-semibold text-white backdrop-blur-sm transition-all min-h-[44px] sm:min-h-0 sm:px-6 sm:py-3 sm:text-base hover:bg-white/30 active:scale-95 sm:active:scale-100 md:hover:scale-105 touch-manipulation flex-shrink-0"
              >
                <FiEdit3 size={18} className="flex-shrink-0" />
                <span>Editar</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Card Principal - Mobile First */}
      <div className="rounded-xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark overflow-hidden sm:rounded-2xl">
        {loading ? (
          <div className="p-3 sm:p-6">
            <Skeleton className="mb-3 h-6 w-3/4 sm:mb-4 sm:h-8" />
            <Skeleton className="mb-2 h-3 w-full sm:h-4" />
            <Skeleton className="mb-2 h-3 w-full sm:h-4" />
            <Skeleton className="mb-2 h-3 w-5/6 sm:h-4" />
            <Skeleton className="mt-3 h-48 w-full sm:mt-4 sm:h-64" />
          </div>
        ) : documentacao ? (
          <div className="p-3 sm:p-6 md:p-8">
            <div className="prose prose-sm sm:prose-base lg:prose-lg dark:prose-invert max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  img: ({ src, alt }) => <MarkdownImage src={src} alt={alt} />,
                  h1: ({ children }) => (
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-3 mt-4 sm:text-3xl sm:mb-4 sm:mt-6 md:text-4xl">
                      {children}
                    </h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2 mt-4 sm:text-2xl sm:mb-3 sm:mt-5 md:text-3xl">
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2 mt-3 sm:text-xl sm:mt-4 md:text-2xl">
                      {children}
                    </h3>
                  ),
                  p: ({ children }) => (
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-3 leading-relaxed sm:text-base sm:mb-4 md:text-lg">
                      {children}
                    </p>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc list-inside mb-3 space-y-1.5 text-gray-700 dark:text-gray-300 sm:mb-4 sm:space-y-2">
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal list-inside mb-3 space-y-1.5 text-gray-700 dark:text-gray-300 sm:mb-4 sm:space-y-2">
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li className="text-sm sm:text-base md:text-lg">{children}</li>
                  ),
                  code: ({ children, className }) => {
                    const isInline = !className;
                    return isInline ? (
                      <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono text-purple-600 dark:text-purple-400 sm:text-sm">
                        {children}
                      </code>
                    ) : (
                      <code className="block bg-gray-100 dark:bg-gray-800 p-3 rounded-lg overflow-x-auto text-xs font-mono text-gray-800 dark:text-gray-200 mb-3 sm:p-4 sm:text-sm sm:mb-4">
                        {children}
                      </code>
                    );
                  },
                  pre: ({ children }) => (
                    <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg overflow-x-auto mb-3 sm:p-4 sm:mb-4">
                      {children}
                    </pre>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-purple-500 pl-3 italic text-gray-600 dark:text-gray-400 my-3 sm:pl-4 sm:my-4">
                      {children}
                    </blockquote>
                  ),
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-600 dark:text-purple-400 hover:underline break-words"
                    >
                      {children}
                    </a>
                  ),
                  table: ({ children }) => (
                    <div className="overflow-x-auto my-3 -mx-1 sm:mx-0 sm:my-4">
                      <table className="min-w-full border-collapse border border-gray-300 dark:border-gray-700 text-sm sm:text-base">
                        {children}
                      </table>
                    </div>
                  ),
                  th: ({ children }) => (
                    <th className="border border-gray-300 dark:border-gray-700 px-2 py-1.5 bg-gray-100 dark:bg-gray-800 font-semibold text-left text-xs sm:px-4 sm:py-2 sm:text-base">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-base">
                      {children}
                    </td>
                  ),
                }}
              >
                {documentacao.text}
              </ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 sm:py-16 px-4">
            <div className="mb-4 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 p-4 dark:from-purple-900/20 dark:to-purple-800/20 sm:mb-6 sm:rounded-2xl sm:p-6">
              <FiBook
                size={36}
                className="text-purple-600 dark:text-purple-400 sm:w-12 sm:h-12"
              />
            </div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white text-center sm:text-base md:text-lg">
              Documentação não encontrada
            </p>
            <button
              type="button"
              onClick={() => navigate("/documentacoes")}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-all min-h-[48px] sm:px-6 sm:py-3 sm:text-base hover:bg-purple-700 active:scale-95 sm:active:scale-100 md:hover:scale-105 touch-manipulation"
            >
              <FiArrowLeft size={18} className="flex-shrink-0" />
              <span>Voltar para lista</span>
            </button>
          </div>
        )}
      </div>

      {/* Modal de Edição */}
      {isAdmin && (
        <DocumentacaoEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          documentacao={documentacao}
          onSuccess={() => {
            loadDocumentacao();
            setIsEditModalOpen(false);
          }}
        />
      )}

      {/* Modal de Imagem em Tela Cheia */}
      {selectedImage && (
        <ImageModal
          imageUrl={selectedImage}
          isOpen={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          alt={documentacao?.title || "Imagem da documentação"}
        />
      )}
    </div>
  );
}

export default DocumentacaoDetail;
